// Eén opgeslagen spelstand met versienummer.
// - Versie 2 (fase 3): euro's als enige munteenheid (portemonnee) en sterren als voortgang.
// - Neemt versie 1 en de oude losse localStorage-waarden ('knus_bezorger_*') over, zodat bestaande
//   spelers niets kwijtraken: munten worden euro's (1 munt = €0,50), sterren worden geschat op
//   4 per eerdere levering.
// Alles wordt gevalideerd: kapotte of verzonnen waarden vallen terug op veilige standaarden.
import type { BoxId, VehicleId } from '../game/economy';
import { PAINTS, UPGRADES } from '../game/economy';
import { SOUND_LEVELS, type SoundLevel } from '../audio/mix';

export const SAVE_KEY = 'bakenbezorg_save_v1';
export { PAINTS };

/** Wisselkoers van oude munten naar euro's. */
export const COIN_TO_EURO = 0.5;
/** Geschatte sterren per levering voor spelers van voor de sterren. */
export const STARS_PER_OLD_DELIVERY = 4;

export type ThemeMode = 'auto' | 'light' | 'dark';
/** Knoppen: links/rechts plus gas en rem. Joystick: richting wijzen plus gas. Eén duim: richting wijzen, gas gaat vanzelf. */
export type ControlMode = 'buttons' | 'joystick' | 'eenduim';

export interface SaveState {
  version: 2;
  /** Te besteden euro's */
  wallet: number;
  /** Alles wat je ooit verdiend hebt (statistiek) */
  lifetimeEarnings: number;
  /** Totaal verdiende sterren: bepaalt rang, upgrades en lakkleuren */
  stars: number;
  deliveredCount: number;
  unlocked: string[];
  box: BoxId;
  wheels: 'standaard' | 'sport';
  engine: 'standaard' | 'turbo';
  paint: string;
  vehicle: VehicleId;
  settings: {
    sound: boolean;
    theme: ThemeMode;
    controls: ControlMode;
    /** Stuurhulp: trekt de scooter zachtjes recht langs de straat */
    assist: boolean;
    /** Trillen bij klappen en meldingen (telefoon) */
    vibrate: boolean;
    /** Kaartje in de hoek */
    minimap: boolean;
    /** Minder beweging: geen schudden, flitsen of klapstops. Automatisch volgt de instelling van je telefoon. */
    motion: 'auto' | 'minder' | 'normaal';
    /** Taal van het spel. Automatisch volgt de taal van het apparaat. */
    language: 'auto' | 'nl' | 'en';
    /** Geluidsmix per kanaal (alleen hoorbaar als geluid aan staat) */
    soundMotor: SoundLevel;
    soundAlarm: SoundLevel;
    soundEffects: SoundLevel;
    /** Kleurenblindveilige route: andere kleuren en daarbij patronen en symbolen */
    colorblind: boolean;
  };
  stats: {
    shifts: number;
    bestEarnings: number;
    bestDeliveries: number;
    bestShiftStars: number;
    distanceM: number;
    raceWins: number;
    stunts: number;
    /** Wijken waar je ooit hebt bezorgd */
    districts: string[];
  };
  /** Behaalde prestaties (ids uit achievements.ts) */
  achievements: string[];
  /** De geleide eerste minuut is afgerond of overgeslagen */
  tutorialDone: boolean;
  /** Dagelijkse uitdaging: datum (JJJJ-MM-DD) en je beste resultaat van die dag */
  daily: { date: string; best: number; played: number };
}

export function defaultSave(): SaveState {
  return {
    version: 2,
    wallet: 15,
    lifetimeEarnings: 0,
    stars: 0,
    deliveredCount: 0,
    unlocked: [],
    box: 'geenbak',
    wheels: 'standaard',
    engine: 'standaard',
    paint: PAINTS[0].code,
    vehicle: 'scooter',
    settings: { sound: true, theme: 'auto', controls: 'buttons', assist: true, vibrate: true, minimap: true, motion: 'auto', language: 'auto', soundMotor: 'normaal', soundAlarm: 'normaal', soundEffects: 'normaal', colorblind: false },
    stats: { shifts: 0, bestEarnings: 0, bestDeliveries: 0, bestShiftStars: 0, distanceM: 0, raceWins: 0, stunts: 0, districts: [] },
    achievements: [],
    tutorialDone: false,
    daily: { date: '', best: 0, played: 0 },
  };
}

const KNOWN_UPGRADES = new Set(UPGRADES.map((u) => u.id));
const KNOWN_DISTRICTS = new Set(['dorpskern', 'campus', 'centrum', 'haven', 'oost', 'industrie', 'zuid']);

const num = (v: unknown, fallback: number, min = 0, max = 1e9) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
};
const oneOf = <T extends string>(v: unknown, options: readonly T[], fallback: T): T =>
  options.includes(v as T) ? (v as T) : fallback;
const strings = (v: unknown, allowed?: Set<string>) =>
  Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && (!allowed || allowed.has(x))))] : [];
const round2 = (n: number) => Math.round(n * 100) / 100;

/** Maakt van willekeurige invoer (versie 1, versie 2 of rommel) een geldige spelstand. */
export function sanitize(input: unknown): SaveState {
  const d = defaultSave();
  if (!input || typeof input !== 'object') return d;
  const s = input as Record<string, any>;
  const isV2 = s.version === 2;

  const deliveredCount = Math.floor(num(s.deliveredCount, 0, 0, 1e7));
  // Versie 1 had munten in plaats van een portemonnee, en nog geen sterren.
  const wallet = isV2 ? num(s.wallet, d.wallet, 0, 1e7) : round2(num(s.coins, 25, 0, 1e7) * COIN_TO_EURO);
  const stars = isV2 ? Math.floor(num(s.stars, 0, 0, 1e7)) : deliveredCount * STARS_PER_OLD_DELIVERY;
  const lifetimeEarnings = num(isV2 ? s.lifetimeEarnings : s.totalEarnings, 0, -1e6, 1e9);

  const unlocked = strings(s.unlocked, KNOWN_UPGRADES);
  let box = oneOf(s.box, ['geenbak', 'kleinebak', 'grotebak'] as const, 'geenbak');
  if (box === 'kleinebak' && !unlocked.includes('box_kleine')) box = 'geenbak';
  if (box === 'grotebak' && !unlocked.includes('box_grote')) box = 'geenbak';
  const wheels = s.wheels === 'sport' && unlocked.includes('wheels_sport') ? 'sport' : 'standaard';
  const engine = s.engine === 'turbo' && unlocked.includes('engine_turbo') ? 'turbo' : 'standaard';
  const vehicle = s.vehicle === 'van' && unlocked.includes('van') ? 'van' : 'scooter';
  const paintDef = PAINTS.find((p) => p.code === s.paint);
  const paint = paintDef && paintDef.minStars <= stars ? paintDef.code : d.paint;

  const settings = s.settings && typeof s.settings === 'object' ? s.settings : {};
  const stats = s.stats && typeof s.stats === 'object' ? s.stats : {};
  const daily = s.daily && typeof s.daily === 'object' ? s.daily : {};

  return {
    version: 2,
    wallet: round2(wallet),
    lifetimeEarnings: round2(lifetimeEarnings),
    stars,
    deliveredCount,
    unlocked,
    box,
    wheels,
    engine,
    paint,
    vehicle,
    settings: {
      sound: settings.sound !== false,
      theme: oneOf(settings.theme, ['auto', 'light', 'dark'] as const, 'auto'),
      controls: oneOf(settings.controls, ['buttons', 'joystick', 'eenduim'] as const, 'buttons'),
      assist: settings.assist !== false,
      vibrate: settings.vibrate !== false,
      minimap: settings.minimap !== false,
      motion: oneOf(settings.motion, ['auto', 'minder', 'normaal'] as const, 'auto'),
      language: oneOf(settings.language, ['auto', 'nl', 'en'] as const, 'auto'),
      soundMotor: oneOf(settings.soundMotor, SOUND_LEVELS, 'normaal'),
      soundAlarm: oneOf(settings.soundAlarm, SOUND_LEVELS, 'normaal'),
      soundEffects: oneOf(settings.soundEffects, SOUND_LEVELS, 'normaal'),
      colorblind: settings.colorblind === true,
    },
    stats: {
      shifts: Math.floor(num(stats.shifts, 0)),
      bestEarnings: num(stats.bestEarnings, 0),
      bestDeliveries: Math.floor(num(stats.bestDeliveries, 0)),
      bestShiftStars: Math.floor(num(stats.bestShiftStars, 0, 0, 3)),
      distanceM: num(stats.distanceM, 0),
      raceWins: Math.floor(num(stats.raceWins, 0)),
      stunts: Math.floor(num(stats.stunts, 0)),
      districts: strings(stats.districts, KNOWN_DISTRICTS),
    },
    achievements: strings(s.achievements),
    // Wie al eens een shift of levering heeft gedaan, hoeft de uitleg niet meer
    tutorialDone: s.tutorialDone === true || Math.floor(num(stats.shifts, 0)) > 0 || deliveredCount > 0,
    daily: {
      date: typeof daily.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(daily.date) ? daily.date : '',
      best: num(daily.best, 0, -1e6, 1e6),
      played: Math.floor(num(daily.played, 0)),
    },
  };
}

/** Zet de oude losse sleutels van "Knus & Krokant — De Bezorger" om naar een spelstand. */
export function migrateLegacy(storage: Pick<Storage, 'getItem'>): SaveState | null {
  const get = (k: string) => {
    try {
      return storage.getItem(k);
    } catch {
      return null;
    }
  };
  const keys = ['knus_bezorger_coins', 'knus_bezorger_deliveries', 'knus_bezorger_earnings', 'knus_bezorger_upgrades', 'knus_bezorger_has_van'];
  if (!keys.some((k) => get(k) !== null)) return null;

  let legacyUpgrades: Record<string, any> = {};
  try {
    legacyUpgrades = JSON.parse(get('knus_bezorger_upgrades') ?? '{}') ?? {};
  } catch {
    legacyUpgrades = {};
  }
  const unlocked: string[] = Array.isArray(legacyUpgrades.unlocked) ? [...legacyUpgrades.unlocked] : [];
  if (get('knus_bezorger_has_van') === 'true') unlocked.push('van');

  // Ziet eruit als een spelstand van versie 1; sanitize zet die om naar versie 2.
  return sanitize({
    coins: get('knus_bezorger_coins') ?? 25,
    totalEarnings: get('knus_bezorger_earnings') ?? 0,
    deliveredCount: get('knus_bezorger_deliveries') ?? 0,
    unlocked,
    box: legacyUpgrades.activeBox,
    wheels: legacyUpgrades.activeWheels === 'wheels_sport' ? 'sport' : 'standaard',
    engine: legacyUpgrades.activeEngine === 'engine_turbo' ? 'turbo' : 'standaard',
    paint: legacyUpgrades.activePaint,
    vehicle: get('knus_bezorger_active_vehicle') === 'van' ? 'van' : 'scooter',
    settings: { sound: true, theme: get('bezorger_theme'), controls: get('bezorger_control_mode') },
  });
}

export function loadSave(storage: Pick<Storage, 'getItem'> | null): SaveState {
  if (!storage) return defaultSave();
  let raw: string | null = null;
  try {
    raw = storage.getItem(SAVE_KEY);
  } catch {
    return defaultSave();
  }
  if (raw) {
    try {
      return sanitize(JSON.parse(raw));
    } catch {
      return defaultSave();
    }
  }
  return migrateLegacy(storage) ?? defaultSave();
}

/** Bewaart de spelstand. Geeft false terug als de opslag vol of geblokkeerd is. */
export function writeSave(storage: Pick<Storage, 'setItem'> | null, state: SaveState): boolean {
  if (!storage) return false;
  try {
    storage.setItem(SAVE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

export type BuyRefusal = 'onbekend' | 'al-gekocht' | 'te-weinig-sterren' | 'te-weinig-geld';

/** Waarom kun je deze upgrade (nog) niet kopen? null = kopen kan. */
export function whyNotBuy(state: SaveState, id: string): BuyRefusal | null {
  const def = UPGRADES.find((u) => u.id === id);
  if (!def) return 'onbekend';
  if (state.unlocked.includes(id)) return 'al-gekocht';
  if (state.stars < def.minStars) return 'te-weinig-sterren';
  if (state.wallet < def.price) return 'te-weinig-geld';
  return null;
}

/** Probeert een upgrade te kopen. Geeft de nieuwe spelstand terug, of null als het niet kan. */
export function buyUpgrade(state: SaveState, id: string): SaveState | null {
  if (whyNotBuy(state, id)) return null;
  const def = UPGRADES.find((u) => u.id === id)!;
  const next: SaveState = { ...state, wallet: round2(state.wallet - def.price), unlocked: [...state.unlocked, id] };
  return equip(next, id) ?? next;
}

/** Zet een vrijgespeelde upgrade of lakkleur aan. */
export function equip(state: SaveState, id: string): SaveState | null {
  const owned = (u: string) => state.unlocked.includes(u);
  const paint = PAINTS.find((p) => p.code === id);
  if (paint) return paint.minStars <= state.stars ? { ...state, paint: paint.code } : null;
  switch (id) {
    case 'box_geen':
      return { ...state, box: 'geenbak' };
    case 'box_kleine':
      return owned(id) ? { ...state, box: 'kleinebak' } : null;
    case 'box_grote':
      return owned(id) ? { ...state, box: 'grotebak' } : null;
    case 'wheels_standaard':
      return { ...state, wheels: 'standaard' };
    case 'wheels_sport':
      return owned(id) ? { ...state, wheels: 'sport' } : null;
    case 'engine_standaard':
      return { ...state, engine: 'standaard' };
    case 'engine_turbo':
      return owned(id) ? { ...state, engine: 'turbo' } : null;
    case 'scooter':
      return { ...state, vehicle: 'scooter' };
    case 'van':
      return owned(id) ? { ...state, vehicle: 'van' } : null;
    default:
      return null;
  }
}

/** Datum van vandaag als JJJJ-MM-DD (lokale tijd), voor de dagelijkse uitdaging. */
export function todayKey(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
